import { Config, AppEnvironment } from '@packages/shared/interfaces';
import { AppName, DEFAULT_LOCALE } from '@packages/shared/core/common/entities';
import { SECOND_IN_MS } from '@packages/shared/core/common/constants';

const resolveEnv = (): AppEnvironment => {
  switch (process.env.NEXT_PUBLIC_ENVIRONMENT) {
    case AppEnvironment.PRODUCTION:
      return AppEnvironment.PRODUCTION;
    case AppEnvironment.STAGE:
      return AppEnvironment.STAGE;
    default:
      return AppEnvironment.DEV;
  }
};

const env = resolveEnv();

const baseConfig: Config = {
  env,
  domainRoot: process.env.NEXT_PUBLIC_DOMAIN_ROOT || '',
  projectName: 'asknebula-funnels',
  appName: AppName.ASKNEBULA,
  domainLocale: DEFAULT_LOCALE,
  appOrigin: {
    default: process.env.NEXT_PUBLIC_DOMAIN_ORIGIN || '',
    asknebula: process.env.NEXT_PUBLIC_ASKNEBULA_ORIGIN || '',
    asknebulaNl: process.env.NEXT_PUBLIC_ASKNEBULA_NL_ORIGIN || '',
    mainApp: process.env.NEXT_PUBLIC_MAIN_APP_ORIGIN || '',
    guidance: process.env.NEXT_PUBLIC_GUIDANCE_ORIGIN || '',
    birthcharts: process.env.NEXT_PUBLIC_BIRTHCHARTS_ORIGIN || '',
  },
  isProduction: env === AppEnvironment.PRODUCTION,
  isStage: env === AppEnvironment.STAGE,
  smartlook: {
    apiKey: process.env.NEXT_PUBLIC_SMARTLOOK_KEY || '',
    setupDelay: SECOND_IN_MS,
  },
  amplitudeApiKey: process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY || '',
  amplitudeProxyUrl: process.env.NEXT_PUBLIC_AMPLITUDE_PROXY_URL || '',
  googleClientId: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || '',
  services: {
    baseUrl: process.env.NEXT_PUBLIC_BASE_API || '',
    askGatewayUrl: process.env.NEXT_PUBLIC_ASK_GATEWAY_API_URL || '',
    expertsDealerUrl: process.env.NEXT_PUBLIC_EXPERTS_DEALER_API_URL || '',
  },
  googleAnalyticsId: process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID || '',
  googleMapApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAP_API_KEY || '',
  googleMapEmbedBaseUrl: 'https://www.google.com/maps/embed/v1/',
  geoCodeUrl: process.env.NEXT_PUBLIC_GEOCODE_URL || '',
  geoCodeApiKey: process.env.NEXT_PUBLIC_GEOCODE_API_KEY || '',
  testingTokenCookieKey: 'tester_token',
  aiSimpleRegFunnel: 'https://aireg.asknebula.com/',
  sentryDsn: process.env.NEXT_PUBLIC_SENTRY_DSN ?? '',
  appVersion: process.env.NEXT_PUBLIC_SENTRY_RELEASE || 'fallback-release',
  contentManager: {
    baseUrl: `${process.env.NEXT_PUBLIC_CONTENT_MANAGER_URL || ''}${process.env.NEXT_PUBLIC_CONTENT_MANAGER_PREFIX || ''}`,
    apiToken: process.env.NEXT_PUBLIC_CONTENT_MANAGER_API_TOKEN || '',
    clientToken: process.env.NEXT_PUBLIC_CONTENT_MANAGER_CLIENT_TOKEN || '',
  },
  growthbook: {
    apiHost: process.env.NEXT_PUBLIC_GROWTHBOOK_API_URL || '',
    clientKey: process.env.NEXT_PUBLIC_GROWTHBOOK_CLIENT_KEY || '',
  },
  googleAnalytics: {
    apiUrl: 'https://www.google-analytics.com',
    measurementId: process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_MEASUREMENT_ID || '',
    apiSecret: process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_API_SECRET || '',
    clientIdKey: process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_CLIENT_ID_KEY || '',
    sessionIdKey: process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_SESSION_ID_KEY || '',
    engagementTime: '100',
  },
  defaultQueryCacheProviderOptions: {
    revalidateOnMount: true,
    revalidateOnFocus: false,
    errorRetryCount: 5,
  },
  buildCache: {
    path: '/.build-request-cache',
  },
  oneTrust: {
    key: process.env.NEXT_PUBLIC_ONE_TRUST_KEY ?? '',
  },
  remoteAssetsStorageUrl: 'https://media.asknebula.com',
  grafanaLogger: {
    url: process.env.NEXT_PUBLIC_GRAFANA_LOGGER_URL ?? '',
  },
  facebookAuthAppId: '996775074296288',
  gtmId: process.env.NEXT_PUBLIC_GTM_ID || 'GTM-PR37WVW',
  observability: {
    defaultTracerName: 'default_tracer',
    tracesUrl: process.env.NEXT_PUBLIC_TRACES_URL ?? '',
    browser: {
      httpInstrumentationIgnoreUrls: [process.env.NEXT_PUBLIC_GRAFANA_LOGGER_URL, /\.clarity\.ms\/collect/gi].filter(
        Boolean,
      ) as Array<string>,
      httpInstrumentationSpanRate: 0.2,
    },
    server: {
      httpInstrumentationIgnoreOutGoingUrls: [],
      httpInstrumentationIgnoreIncomingUrls: [
        /api\/____service\/healthcheck/,
        /next\/image/,
        /next\/static/,
        /next\/data/,
        /images/,
        /favicon/,
      ],
      httpInstrumentationSpanRate: 0.2,
    },
  },
  clarityId: process.env.NEXT_PUBLIC_CLARITY_ID || '',
  supportBotApiKey: 'b57b661b-8a7f-40e1-9e62-0f2630788e9f',
  verification: {
    trustpilotVerificationId: 'ea688f7d-9420-4940-b046-f90ff48348a7',
    facebookVerificationId: 'ld6fauq8wysgqi8nxb014927mbq37x',
  },
  trustpilot: {
    businessUnitId: '640ef3de4e2a6ee279ad8d16',
    token: 'fac1db1b-382f-4c72-ba2e-148f67b6852f',
    templateId: '5419b6ffb0d04a076446a9af',
    reviewUrl: 'https://www.trustpilot.com/review/asknebula.com',
  },
};

export const config: Config = baseConfig;
